/**
 * CommandMenu — slash-command discoverability overlay.
 *
 * Spec 26 C3. Ports variant-c-scenes.jsx::CC_CommandMenu — a
 * 3-column grouped menu (Navigate / Filter / Act) that surfaces
 * every slash command + its description + optional keybind hint.
 *
 * Owned by CommandRail; this component is just the rendering.
 * Selection callback receives the full command string (e.g.
 * "/spine"); rail prefills the input + closes the menu so the
 * writer can edit any placeholder before submit.
 *
 * Accessibility: role="menu" + role="menuitem"; keyboard arrow
 * navigation is delegated to the rail's input focus model — the
 * writer can either click an item or keep typing.
 *
 * SRP: pure presentation. The catalog comes from commands/menu.ts;
 * the close + select decisions come from the parent.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import type { MenuGroup, MenuItem } from "../commands/menu";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

export interface CommandMenuProps {
  /** State-aware + query-filtered groups the rail composed. The
   *  menu is pure rendering; the parent decides what's visible. */
  groups: ReadonlyArray<MenuGroup>;
  /** Flat-index of the keyboard-highlighted item. Pairs with
   *  ``flattenMenuItems(groups)``: index 0 is the first item of
   *  the first group, index 1 the next item, etc. The rail owns
   *  the index + updates it on Arrow Up/Down. Negative / out-of-
   *  range → no highlight. */
  highlightedIndex: number;
  /** Click handler — receives the menu item the writer picked.
   *  Rail prefills the input with item.cmd then closes. */
  onSelect: (item: MenuItem) => void;
  /** Click handler for the backdrop / Escape. Parent maintains
   *  the open state. */
  onClose: () => void;
  /** Fired when the pointer enters an item. Pass-through to the
   *  rail's highlightedIndex so keyboard and mouse stay in sync —
   *  hovering an item then pressing Enter picks the hovered item,
   *  not whatever was previously keyboard-highlighted. Optional;
   *  when omitted the menu behaves as keyboard-only. */
  onHoverItem?: (flatIndex: number) => void;
}

const useStyles = makeStyles({
  root: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    right: 0,
    zIndex: 5,
    background: "#fff",
    borderTop: `1px solid ${surfaceColors.lineStrong}`,
    borderBottom: `1px solid ${surfaceColors.line}`,
    boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.04)",
    fontFamily: fonts.body,
  },
  contextLine: {
    padding: "6px 12px",
    fontSize: "10px",
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.mono,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  contextLabel: {
    color: inkColors.ink4,
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  spacer: {
    flex: 1,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    cursor: "pointer",
    padding: "0 4px",
    "&:hover": {
      color: inkColors.ink2,
    },
  },
  // Post-Spec-29 palette merge: groups stack vertically — the
  // flat 3-column layout broke when the catalog grew to 6 groups.
  // A max-height + scroll handles tall menus on narrow panes.
  groups: {
    display: "flex",
    flexDirection: "column",
    padding: "6px 0",
    maxHeight: "340px",
    overflowY: "auto",
  },
  group: {
    padding: "4px 10px 6px",
    minWidth: 0,
  },
  groupDivider: {
    borderTop: `1px dashed ${surfaceColors.line}`,
    paddingTop: "6px",
  },
  emptyState: {
    padding: "14px 16px",
    fontSize: "11px",
    color: inkColors.ink4,
    fontStyle: "italic",
    textAlign: "center",
  },
  groupLabel: {
    fontSize: "9px",
    fontWeight: 700,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: "4px",
  },
  item: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    padding: "4px 6px",
    fontSize: "10.5px",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    borderRadius: "3px",
    "&:hover": {
      background: surfaceColors.paperDim,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-1px",
    },
  },
  // Applied alongside ``item`` when MenuItem.disabled === true:
  // dim the row, hide the pointer-cursor affordance, and suppress
  // the hover highlight so the writer doesn't get a "clickable"
  // signal. Click is additionally blocked at the handler — this is
  // the visual half of the same contract.
  itemDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
    "&:hover": {
      background: "transparent",
    },
  },
  // Keyboard-highlighted item. Applied alongside ``item`` when
  // the rail's highlightedIndex matches this flat-index. Uses the
  // brand-light tint so hover + highlight look similar (familiar
  // "this is the active row" signal) but the highlight persists
  // across pointer events.
  itemHighlighted: {
    background: brandColors.light,
  },
  itemCmd: {
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: brandColors.primary,
    minWidth: "84px",
    flexShrink: 0,
  },
  itemDesc: {
    flex: 1,
    color: inkColors.ink2,
    fontSize: "10px",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemKey: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    border: `1px solid ${surfaceColors.line}`,
    padding: "0 4px",
    borderRadius: "2px",
    flexShrink: 0,
  },
});

export const CommandMenu: React.FC<CommandMenuProps> = ({
  groups,
  highlightedIndex,
  onSelect,
  onClose,
  onHoverItem,
}) => {
  const styles = useStyles();
  const empty = groups.length === 0;
  // Running flat-index so each rendered item can compare against
  // ``highlightedIndex``. Incremented once per item across groups.
  let flatIdx = -1;
  return (
    <div className={styles.root} role="menu" aria-label="Slash-command menu">
      <div className={styles.contextLine}>
        <span className={styles.contextLabel}>Commands</span>
        <span>type / to filter · click to insert · Esc to close</span>
        <span className={styles.spacer} />
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close command menu">
          ×
        </button>
      </div>
      <div className={styles.groups}>
        {empty && <output className={styles.emptyState}>No commands match. Try a different term or press Esc.</output>}
        {groups.map((group, gi) => (
          <div key={group.label} className={gi > 0 ? `${styles.group} ${styles.groupDivider}` : styles.group}>
            <div className={styles.groupLabel}>{group.label}</div>
            {group.items.map((item) => {
              flatIdx += 1;
              const isDisabled = item.disabled === true;
              const isHighlighted = flatIdx === highlightedIndex;
              const tooltip = isDisabled
                ? item.disabledReason
                  ? `${item.desc} — ${item.disabledReason}`
                  : `${item.desc} (not available yet)`
                : item.desc;
              const className = [
                styles.item,
                isDisabled ? styles.itemDisabled : "",
                isHighlighted && !isDisabled ? styles.itemHighlighted : "",
              ]
                .filter(Boolean)
                .join(" ");
              // Capture the flat index at closure-creation time so
              // the hover handler always reports this item's index,
              // not the loop's final value.
              const hoverIdx = flatIdx;
              return (
                <button
                  key={item.cmd}
                  type="button"
                  role="menuitem"
                  className={className}
                  onClick={isDisabled ? undefined : () => onSelect(item)}
                  onMouseEnter={
                    onHoverItem && !isDisabled
                      ? () => onHoverItem(hoverIdx)
                      : undefined
                  }
                  onMouseDown={(e) => { e.preventDefault()}} /* keep focus on input */
                  aria-disabled={isDisabled || undefined}
                  aria-selected={isHighlighted || undefined}
                  title={tooltip}
                >
                  <span className={styles.itemCmd}>{item.cmd}</span>
                  <span className={styles.itemDesc}>{item.desc}</span>
                  {item.keybind && <span className={styles.itemKey}>{item.keybind}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommandMenu;
